import { useEffect, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { CHAT_VIEW_NAME, EDITOR_VIEW_NAME } from "../../core/constants";
import { getDomainCfg, store } from "../../core/state";
import { getViewData, setViewData } from "../../core/views";
import { translate } from "../../core/i18n";
import * as api from "../../features/chatrequests";
import { saveUserData } from "../../core/code/saver";
import { userHasRoleFromList } from "../../features/cloudstorage/user";
import { useAuth } from "../../hooks/useAuth";
import { saveV2MetaToLS } from "../../features/localstorage/saver";
import { listFiles, readFile, writeFile } from "../../features/agent/agentTools";
import { getLanguageFromFilename } from "../../features/filesystem";
import { ToolInvocationAccordion } from "./ToolInvocationAccordion";
import { AgentMessageContent } from "./AgentMessageContent";
import "../../styles/BaseChat.scss";

interface MessagePart {
  type: string;
  text?: string;
  toolInvocation?: {
    state: string;
    toolName?: string;
    toolCallId?: string;
    args?: Record<string, unknown>;
    result?: unknown;
  };
}

interface UIMessageLike {
  id: string;
  role: string;
  content?: string;
  parts?: MessagePart[];
}

export interface AgentChatProps {
  projectId: string | undefined;
}

const maxLines = 8;

export function AgentChat({ projectId }: AgentChatProps) {
  const [input, setInput] = useState("");
  const [lineCount, setLineCount] = useState(2);
  const [isUserAtBottom, setIsUserAtBottom] = useState(true);
  const [prevMessagesLength, setPrevMessagesLength] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const quotaBeforeRef = useRef<number | null>(null);
  const chatUrl = getDomainCfg().chatSettings?.chatUrl ?? "";
  const apiUrl = chatUrl.replace(/\/$/, "") + "/v1/chat/agent";

  // Auth beim Öffnen des Agent-Tabs (wie alter Chat mit useAuth) → chatkasten /auth sichtbar
  useAuth();

  const [chatState] = store.useState(CHAT_VIEW_NAME);
  const editorState = getViewData(EDITOR_VIEW_NAME);
  const llmSwitchAvailable = chatState?.cfg?.llmSwitchAvailable === true;
  const modelOptions: { id: string; name: string; roles?: string[] }[] = Array.isArray(chatState?.cfg?.switchModels)
    ? chatState.cfg.switchModels
    : [];
  const isModelAvailable = (model: { id: string; name: string; roles?: string[] }): boolean => {
    if (!model.roles || !Array.isArray(model.roles) || model.roles.length === 0) return true;
    return userHasRoleFromList(model.roles);
  };
  const getFirstAvailableModel = (): string | undefined =>
    modelOptions.find((opt) => isModelAvailable(opt))?.id;
  const savedModelId = editorState?.data?.lastUsedModelId;
  const savedModel = savedModelId ? modelOptions.find((opt) => opt.id === savedModelId) : null;
  const initialModelId =
    savedModel && isModelAvailable(savedModel) ? savedModelId : getFirstAvailableModel();
  const [selectedModelId, setSelectedModelId] = useState<string | undefined>(initialModelId);

  useEffect(() => {
    if (savedModelId && savedModel && isModelAvailable(savedModel)) {
      setSelectedModelId(savedModelId);
    } else if (!selectedModelId && getFirstAvailableModel()) {
      setSelectedModelId(getFirstAvailableModel());
    }
  }, [modelOptions.length, savedModelId]);

  const chatId = projectId
    ? (editorState?.data?.agentChatId ?? `agent-chat-${projectId}`)
    : "agent-chat-no-project";
  const initialMessages = projectId ? (editorState?.data?.agentChatMessages ?? []) : [];

  const bodyWithModel = {
    agent: "build",
    ...(llmSwitchAvailable && selectedModelId ? { modelId: selectedModelId } : {}),
  };
  const { messages, status, append, error } = useChat({
    id: chatId,
    initialMessages,
    api: apiUrl,
    body: bodyWithModel,
    maxSteps: 20,
    fetch: async (url, init) => {
      const sessionBefore = await api.getSession();
      if (sessionBefore?.quota?.credits != null) quotaBeforeRef.current = sessionBefore.quota.credits;
      let token = localStorage.getItem("sessionToken");
      if (!token) {
        const session = await api.authenticateUser();
        if (session?.error) throw new Error(session.message || "Auth failed");
        token = localStorage.getItem("sessionToken");
      }
      const headers = new Headers(init?.headers);
      if (token) headers.set("Authorization", `Bearer ${token}`);
      const res = await fetch(url, { ...init, headers });
      if (res.status === 401) {
        localStorage.removeItem("sessionToken");
        await api.authenticateUser();
        throw new Error("Session abgelaufen. Bitte Nachricht erneut senden.");
      }
      if (!res.ok) {
        const text = await res.text();
        console.error("[AgentChat API]", res.status, text);
        throw new Error(text || res.statusText || `HTTP ${res.status}`);
      }
      return res;
    },
    onError: (err) => console.error("[AgentChat]", err),
    async onToolCall({ toolCall }) {
      if (!projectId) return { error: "No project loaded" };
      const { toolName, args } = toolCall;
      const a = (args ?? {}) as Record<string, unknown>;
      const name =
        toolName === "listFiles" ? "list_files" : toolName === "readFile" ? "read_file" : toolName === "writeFile" ? "write_file" : toolName;

      if (name === "list_files") {
        const path = typeof a.path === "string" ? a.path : "/";
        const result = await listFiles(projectId, path);
        return result.error ? { error: result.error } : { entries: result.entries };
      }
      if (name === "read_file") {
        const path = typeof a.path === "string" ? a.path : "";
        const result = await readFile(projectId, path);
        if (result.error) return { error: result.error };
        return { content: result.content };
      }
      if (name === "write_file") {
        const path = typeof a.path === "string" ? a.path : "";
        const content = typeof a.content === "string" ? a.content : "";
        const result = await writeFile(projectId, path, content);
        if (result.error) return { error: result.error };
        // Editor-UI aktualisieren: v2EditorChanges setzen + (falls nötig) neue Datei-Definitionen upserten
        const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
        const fileName = (normalizedPath.split("/").pop() || normalizedPath || "file").trim();

        if (normalizedPath || path) {
          const current = getViewData(EDITOR_VIEW_NAME);

          const prev = current?.data?.v2EditorChanges;
          const changes: Record<string, string> =
            prev instanceof Map ? Object.fromEntries(prev) : { ...(prev || {}) };

          if (normalizedPath) changes[normalizedPath] = content;
          // Backward-compat: falls irgendwo mit führendem Slash gespeichert wurde
          if (path && path !== normalizedPath) changes[path] = content;

          const currentTabs = current?.data?.projectContent?.tabs || [];
          const pathInTabs =
            currentTabs?.find((t: any) => t?.path === normalizedPath || (path && t?.path === path)) || null;

          let tabsChanged = false;
          let nextTabs = currentTabs;

          if (!pathInTabs && normalizedPath) {
            tabsChanged = true;
            const nextIndex = currentTabs.length;
            const newTab = {
              index: nextIndex,
              path: normalizedPath,
              code: "",
              active: false,
              name: fileName,
              language: getLanguageFromFilename(fileName),
              replaceKey: "",
            } as any;

            nextTabs = [...currentTabs, newTab];
          }

          setViewData(EDITOR_VIEW_NAME, {
            ...current,
            data: {
              ...current.data,
              ...(current.data?.projectContent
                ? { projectContent: { ...current.data.projectContent, tabs: nextTabs } }
                : { projectContent: { tabs: nextTabs } }),
              v2EditorChanges: changes,
            },
          });

          // Nur Meta-Daten persistieren, damit Reload den neuen Pfad wieder kennt
          if (tabsChanged) saveV2MetaToLS();
        }
        return { ok: true };
      }
      return { error: "Unknown tool" };
    },
  });

  const isLoading = status === "streaming" || status === "submitted";
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debug: Quota vorher/nachher nach abgeschlossenem Agent-Request loggen
  useEffect(() => {
    if (status !== "ready" || quotaBeforeRef.current === null) return;
    api.getSession().then((sessionAfter) => {
      const after = sessionAfter?.quota?.credits ?? null;
      const before = quotaBeforeRef.current;
      console.log(
        "[AgentChat] Quota vorher:",
        before,
        "nachher:",
        after,
        "Differenz (abgezogen):",
        before - (after ?? 0)
      );
      quotaBeforeRef.current = null;
    });
  }, [status]);

  // Persist agent chat when messages change (debounced, only when ready)
  useEffect(() => {
    if (!projectId || status !== "ready" || messages.length === 0) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      const current = getViewData(EDITOR_VIEW_NAME);
      const updated = {
        ...current,
        data: {
          ...current.data,
          agentChatMessages: messages,
          agentChatId: chatId,
        },
      };
      setViewData(EDITOR_VIEW_NAME, updated);
      await saveUserData();
      saveTimeoutRef.current = null;
    }, 5000);
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [projectId, messages, status, chatId]);

  useEffect(() => {
    const el = messagesEndRef.current;
    if (!el) return;
    const handleScroll = () => {
      const atBottom =
        el.scrollHeight - el.clientHeight <= el.scrollTop + 1;
      setIsUserAtBottom(atBottom);
    };
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isUserAtBottom && (isLoading || messages.length > prevMessagesLength)) {
        const el = messagesEndRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      }
      setPrevMessagesLength(messages.length);
    }, 300);
    return () => clearTimeout(timer);
  }, [messages.length, isLoading, isUserAtBottom, prevMessagesLength]);

  const selectedModel = selectedModelId ? modelOptions.find((opt) => opt.id === selectedModelId) : null;
  const modelNotAvailable =
    !!selectedModel && llmSwitchAvailable && !isModelAvailable(selectedModel);

  const handleModelChange = async (modelId: string) => {
    setSelectedModelId(modelId);
    const current = getViewData(EDITOR_VIEW_NAME);
    const updated = {
      ...current,
      data: { ...current.data, lastUsedModelId: modelId },
    };
    setViewData(EDITOR_VIEW_NAME, updated);
    await saveUserData();
    const model = modelOptions.find((opt) => opt.id === modelId);
    if (model && !isModelAvailable(model)) {
      const upgradeUrl = chatState?.cfg?.upgradeUrl || "https://app.vibeventure.de/produkte/";
      window.open(upgradeUrl, "_blank");
    }
  };

  const sendMessage = () => {
    const text = input.trim();
    if (!text || isLoading) return;
    if (modelNotAvailable) {
      const upgradeUrl = chatState?.cfg?.upgradeUrl || "https://app.vibeventure.de/produkte/";
      window.open(upgradeUrl, "_blank");
      return;
    }
    append({ role: "user", content: text }, { body: bodyWithModel });
    setInput("");
    setLineCount(2);
    setIsUserAtBottom(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage();
  };

  const newMessageChanged = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const lines = e.target.value.split("\n").length;
    setLineCount(Math.max(2, Math.min(lines, maxLines)));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
    if (e.key === "Enter" && e.shiftKey && lineCount < maxLines) {
      setLineCount((c) => Math.min(maxLines, c + 1));
    }
  };

  const lastMessage = messages[messages.length - 1];
  const lastMessageId = lastMessage?.id;

  return (
    <div className="chatContainer d-flex flex-column h-100 overflow-hidden">
      <div className="chatMessages flex-grow-1 overflow-auto p-2" ref={messagesEndRef}>
        {messages.length === 0 ? (
          <div className="text-muted text-center py-4 small">
            <p className="mb-0">KiRa AI Agent</p>
            <p className="mb-0">Nachricht eingeben. Der Agent kann Projektdateien lesen und schreiben.</p>
          </div>
        ) : (
          messages
            .filter((msg) => (msg as UIMessageLike).role !== "system")
            .map((msg) => {
              const uiMsg = msg as UIMessageLike;
              const parts = uiMsg.parts ?? (uiMsg.content ? [{ type: "text" as const, text: uiMsg.content }] : []);
              const isLast = uiMsg.id === lastMessageId;
              return (
                <div
                  key={uiMsg.id}
                  className={`mb-3 p-2 rounded ${uiMsg.role === "user" ? "bg-primary bg-opacity-10 ms-3" : "bg-light me-3"}`}
                >
                  <span className="small fw-bold text-muted">{uiMsg.role === "user" ? "Du" : "Agent"}</span>
                  {parts.map((part, i) => {
                    if (part.type === "text" && part.text != null) {
                      return (
                        <AgentMessageContent
                          key={i}
                          id={`${uiMsg.id}-part-${i}`}
                          message={part.text}
                        />
                      );
                    }
                    if (part.toolInvocation) {
                      return <ToolInvocationAccordion key={i} inv={part.toolInvocation} />;
                    }
                    return null;
                  })}
                  {isLast && isLoading && <span className="small text-muted"> …</span>}
                </div>
              );
            })
        )}
      </div>
      {error && (
        <div className="alert alert-danger alert-sm m-2 mb-0 py-2 small">
          {error.message}
        </div>
      )}
      <div className="chatInput">
        <div className="chatInputWrapper">
          <textarea
            className="form-control chatInputTextarea"
            placeholder="Nachricht an den Agenten… (Enter = Senden, Shift+Enter = neue Zeile)"
            value={input}
            onChange={newMessageChanged}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            style={{ minHeight: `${30 * Math.max(lineCount, 2)}px` }}
          />
          <div className="chatInputControls">
            {llmSwitchAvailable && modelOptions.length > 0 && (
              <select
                className="form-select chatModelSelect"
                value={selectedModelId || modelOptions[0]?.id}
                onChange={(e) => handleModelChange(e.target.value)}
                disabled={isLoading}
              >
                {modelOptions.map((opt) => {
                  const available = isModelAvailable(opt);
                  return (
                    <option
                      key={opt.id}
                      value={opt.id}
                      style={!available ? { color: "#999", fontStyle: "italic" } : {}}
                    >
                      {available ? opt.name : `${opt.name} (Upgrade)`}
                    </option>
                  );
                })}
              </select>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={sendMessage}
              disabled={isLoading || !input.trim() || modelNotAvailable}
              title={
                modelNotAvailable
                  ? translate("chat-model-not-available-tooltip") ||
                    "Dieses Modell ist nicht in Ihrem aktuellen Plan enthalten. Bitte upgraden Sie Ihren Plan."
                  : ""
              }
              data-testid="send-message-button"
            >
              {isLoading ? "…" : "Senden"}
            </button>
          </div>
        </div>
      </div>
      <p className="text-center disclaimer">{getDomainCfg().chatSettings?.disclaimer ?? ""}</p>
    </div>
  );
}
